import React, { useEffect, useRef, useState } from "react";
import _ from 'lodash'
import { useNavigation, useRoute } from "@react-navigation/native";
import i18n from "utils/i18n";
import { PlaceholderBooking, PlaceholderText } from "placeholders";
import ThemedSafeAreaComponentTop from "components/atoms/ThemedSafeAreaTop";
import ThemedLayout, { createRefreshControl } from "components/atoms/ThemedLayout";
import { StyleSheet, View } from "react-native";
import Text from "theme/Text";
import GraphqlBuilder from "utils/request/graphql/builder";
import QueryEmployeeBookingInvitationSent from "graphql/queries/QueryEmployeeBookingInvitationSent";
import MutationEmployeeBookingInvitation from "graphql/mutations/MutationEmployeeBookingInvitation";
import ObjectBookingInvitation from "utils/objects/booking-invitation";
import InvitationBookings from "components/molecules/InvitationBookings";
import { constantsActive } from "utils/constants/constants";
import ThemedBlock from 'components/atoms/ThemedBlock'
import MutationEmployeeBookingStatus from "graphql/mutations/MutationEmployeeBookingStatus";
import Button from "theme/Button";
import TextDivider from 'components/atoms/TextDivider'
import ThemedBlockTitle from 'components/atoms/ThemedBlockTitle'
import HelperString from 'utils/helper-string'
import FetchCachedBookingInvitationSent from 'utils/fetch/cached/FetchCachedBookingInvitationSent'
import ThemedButtonContainer from 'components/atoms/ThemedButtonContainer'
import LoadingComponent from 'components/atoms/Loading'
import ThemedList from 'components/atoms/ThemedList'
import ListItemLog from 'scenes/home/invitation/_components/ListItemLog'
import FetchCachedEmployee from 'utils/fetch/cached/FetchCachedEmployee'
import { AppRoutes } from 'navigations/app-routes'
import HelperAlerts from 'utils/HelperAlerts'
import HelperDate from 'utils/helper-date'
import VStack from 'components/atoms/stack/VStack'

const HomeInvitationSent = () => {
	
	const route = useRoute();
	const navigation = useNavigation();
	
	const [ componentKey, setComponentKey ] = useState( HelperString.random() )
	const [ saving, setSaving ] = useState( false )
	const [ loading, setLoading ] = useState( true )
	const [ refreshing, setRefreshing ] = useState( false )
	const [ archiving, setArchiving ] = useState( false )
	const [ editing, setEditing ] = useState( false )
	
	const overwriteEmployeeStatus = useRef( {} )
	const [ canSave, setCanSave ] = useState( false )
	const [ employeeBookingInvitation, setEmployeeBookingInvitation ] = useState( {} )
	const [ organizer, setOrganizer ] = useState( {} )
	const [ bookingsByDate, setBookingsByDate ] = useState( {} )
	const [ employees, setEmployees ] = useState( {} )
	
	const employeeBookingInvitationGuid = _.get( route, ['params', 'employee_booking_invitation_guid' ], false )
	
	const setOverwriteEmployeeStatus = ( status ) => {
		overwriteEmployeeStatus.current = status;
	}
	
	const onInvitationBookingSaveChanges = () => {
		// Remove the guids together
		const graphqlBuilder = new GraphqlBuilder();
		
		_.forEach( overwriteEmployeeStatus.current, ( status, guid ) => {
			graphqlBuilder.addQuery( `mutation_employee_booking_status_${guid}`, MutationEmployeeBookingStatus, {
				guid: guid,
				active: constantsActive.YES,
				status: status
			})
		});
		
		graphqlBuilder.setOnStart( setSaving.bind( this, true ) );
		
		graphqlBuilder.setOnSuccess( (result) => {
			setOverwriteEmployeeStatus( {} )
			setCanSave( false )
			setEditing( false )
			setSaving( false )
			
			fetch()
		});
		
		graphqlBuilder.setOnError( () => {
			setOverwriteEmployeeStatus( {} )
			setEditing( false )
			setSaving( false )
		});
		
		graphqlBuilder.execute()
	}
	
	const fetch = () => {
		new GraphqlBuilder()
			.addQuery( 'booking_invite_overview', QueryEmployeeBookingInvitationSent, {
				guid: employeeBookingInvitationGuid,
				with_inviter: constantsActive.YES,
				booking_active: [
					constantsActive.YES,
					constantsActive.NO,
				]
			})
			.setResultPath( ['booking_invite_overview', 'employee_booking_invitation'], {} )
			.setOnStart( setLoading.bind( this ,true ) )
			.setOnSuccess( result => {
				const organizer = _.get( result, 'employee', {} );
				
				const { employees, bookingsByDate } = ObjectBookingInvitation.parse( result, {
					onlyMyBookings: false
				} );
				
				const now = HelperDate.date();
				
				let isInFuture = false;
				_.forEach( bookingsByDate, ( value, date ) => {
					const dateObject = HelperDate.date( date );
					if( dateObject.isSameOrAfter( now, 'day' ) ) {
						isInFuture = true;
						return false; // break out of the loop
					}
				});
				
				setLoading( false )
				setRefreshing( false )
				setEmployeeBookingInvitation( result )
				setOrganizer( organizer )
				setEmployees( employees )
				setBookingsByDate( bookingsByDate )
				setComponentKey( HelperString.random() )
			})
			.setOnError( result => {
				setLoading( false )
				setRefreshing( false )
			})
			.execute()
	}
	
	const fetchEmployees = () => {
		FetchCachedEmployee.fetchEasy( {
			employee_guids: _.map( employees, o => {
				return o.guid;
			} )
		});
	}
	
	const onCancelChanges = () => {
		setEditing( false )
		setOverwriteEmployeeStatus( {} )
	}
	
	const onChangeEmployee = ( bookingGuid, employeeGuid, status ) => {
		setOverwriteEmployeeStatus( {
			...overwriteEmployeeStatus.current,
			[ bookingGuid ]: status
		})
		
		setCanSave( Object.keys( overwriteEmployeeStatus.current ).length > 0 )
	}
	
	const showRevokeAlert = () => {
		HelperAlerts.alert(
			i18n.t( 'page.invitation.overview.alert.remove.title' ),
			i18n.t( 'page.invitation.overview.alert.remove.description' ),
			[
				{
					text: i18n.t( 'basic.cancel' ),
				},
				{
					text: i18n.t( 'page.invitation.overview.alert.remove.confirm' ),
					onPress: onRevoke.bind( this )
				}
			]
		);
	}
	
	const onRevokeDone = () => {
		setArchiving( false )
		navigation.goBack();
	}
	
	const onRevoke = () => {
		new GraphqlBuilder()
			.addQuery('mutation_employee_booking_invitation', MutationEmployeeBookingInvitation, {
				guid: employeeBookingInvitation.guid,
				active: constantsActive.NO
			})
			.setResultPath( ['mutation_employee_booking_invitation', 'employee_booking_invitation'], {} )
			.setOnStart( setArchiving.bind( this, true ) )
			.setOnSuccess( result => {
				FetchCachedBookingInvitationSent.reload({
					callbackSuccess: onRevokeDone.bind( this ),
					callbackError: onRevokeDone.bind( this ),
				});
			})
			.setOnError( setArchiving.bind( this, false ) )
			.execute()
	}
	
	const openInvitationLogs = () => {
		navigation.navigate( AppRoutes.INVITATION_LOGS, {
			employee_booking_invitation_guid: employeeBookingInvitationGuid,
		})
	}
	
	useEffect( () => {
		return fetchEmployees();
	}, [ employees ] )
	
	useEffect( () => {
		return fetch();
	}, [] )
	
	// Invitation name and description
	const invitationName = _.get( employeeBookingInvitation, 'name', null );
	const invitationDescription = _.get( employeeBookingInvitation, 'description', null );
	
	const logs = _.get( employeeBookingInvitation, 'logs', [] )
	
	return (
		<ThemedSafeAreaComponentTop
			top={ false }>
			
			<ThemedLayout
				title={ i18n.t('page.invitation.overview.title') }
				topNavigation={ true }
				showBackButton={ true }
				footerBar={ false }
				scrollViewProps={{
					...createRefreshControl({
						isRefreshing: refreshing,
						onRefresh: () => {
							setRefreshing( true )
							fetch()
						}
					})
				}}>
				
				{ loading && (
					<VStack>
						<PlaceholderText />
						<PlaceholderBooking />
					</VStack>
				)}
				
				{/* Information */}
				<View>
					<TextDivider
						rowStyle={ styles.textDividerMarginTop12 }
						text={ i18n.t('page.booking.invite.title') }
					/>
					
					{/* Create the block*/}
					<ThemedBlock
						smallPadding
						shadow>
						
						<Text>
							{ i18n.t('page.invitation.overview.description', {
								amount: _.size( employees )
							}) }
						</Text>
					</ThemedBlock>
				</View>
				
				{/*  */}
				{ (invitationName !== null || invitationDescription !== null) && (
					<View>
						<TextDivider
							rowStyle={ styles.textDividerMarginTop12}
							text={ i18n.t( 'constants.information' ) }
						/>
						
						<ThemedBlock
							smallPadding
							shadow>
							
							{ invitationName !== null && (
								<ThemedBlockTitle
									style={ invitationDescription === null ? styles.themedBlockTitleMarginBottom0 : {} }>
									
									{ invitationName }
								</ThemedBlockTitle>
							)}
							
							{ invitationDescription !== null && (
								<Text>
									{ invitationDescription }
								</Text>
							)}
						</ThemedBlock>
					</View>
				)}
				
				{/* Bookings */}
				<InvitationBookings
					key={ HelperString.hash('invitation-bookings', componentKey )}
					editing={ editing }
					organizer={ organizer }
					bookingsByDate={ bookingsByDate }
					employees={ employees }
					onPressEmployee={ onChangeEmployee.bind( this ) }
					overwriteEmployeeStatus={ overwriteEmployeeStatus.current }
				/>
				
				{/* The log */}
				<View>
					<TextDivider
						rowStyle={ styles.textDividerMarginTop12 }
						text={ i18n.t('basic.activity') }
					/>
					
					<ThemedBlock
						footer={(
							<Button
								style={ styles.buttonFlex0WidthAuto }
								size={'small'}
								appearance={'ghost'}
								onPress={ openInvitationLogs.bind( this ) }>
								
								{ i18n.t('basic.more') }
							</Button>
						)}
						footerStyle={ styles.themedBlockJustifyContentFlexEndBorderBottomWidth0 }
						shadow>
						
						<ThemedList
							data={ logs }
							renderItem={ ListItemLog }
							
							style={ styles.themedListPaddingHorizontal0 }
						/>
					</ThemedBlock>
				</View>
				
				{/* Buttons */}
				<ThemedButtonContainer>
					<Choose>
						<When condition={ !editing }>
							<Button
								appearance={"outline"}
								style={ styles.buttonFlex50}
								onPress={ setEditing.bind( this, true ) }>
								
								{ i18n.t( 'constants.edit' ) }
							</Button>
						</When>
						<Otherwise>
							<Button
								isLoading={ saving }
								disabled={ !canSave }
								style={ styles.buttonFlex50}
								onPress={ onInvitationBookingSaveChanges.bind( this ) }>
								
								{ i18n.t( 'page.invitation.overview.save' ) }
							</Button>
						</Otherwise>
					</Choose>
				
					<Choose>
						<When condition={ !editing }>
							<Button
								status={ 'danger' }
								appearance={"ghost"}
								style={ styles.buttonFlex50}
								onPress={ showRevokeAlert.bind( this ) }>
								
								{ i18n.t( 'page.invitation.overview.actions.revoke' ) }
							</Button>
						</When>
						<Otherwise>
							<Button
								status={"danger"}
								appearance={"ghost"}
								disabled={ saving }
								style={ styles.buttonFlex50}
								onPress={ onCancelChanges.bind( this ) }>
								
								{ i18n.t( 'page.invitation.overview.cancel' ) }
							</Button>
						</Otherwise>
					</Choose>
				</ThemedButtonContainer>
			</ThemedLayout>
			
			{/* Is updating */}
			{ archiving && (
				<LoadingComponent fullscreen={ true } size={ 'l' } />
			)}
		</ThemedSafeAreaComponentTop>
	)
}

export const styles = StyleSheet.create({
	textDividerMarginTop12: {
		marginTop: 12,
	},
	themedBlockTitleMarginBottom0: {
		marginBottom: 0,
	},
	buttonFlex0WidthAuto: {
		flex: 0,
		width: 'auto',
		minWidth: 'auto',
	},
	themedBlockJustifyContentFlexEndBorderBottomWidth0: {
		justifyContent: 'flex-end',
		borderBottomWidth: 0,
	},
	themedListPaddingHorizontal0: {
		paddingHorizontal: 0,
	},
	buttonFlex50: {
		flex: 50,
	},
	messageContainer: {
		marginVertical: 0,
		paddingVertical: 0,
	},
});

export default HomeInvitationSent
